import { faker } from '@faker-js/faker/locale/en'
import { DateTime } from 'luxon'
import Customer from './customers'
import type {
  GETCapacityBucket,
  GETCapacitySet,
  PortComplex,
  SavedCustomer,
  SimpleUser,
} from '~/services/apiClient'
import { TransactionDirection } from '~/services/apiClient'
import { convertMinuteOfWeekToTime } from '~/components/capacity/helpers'
import { getPortLabel } from '~/constants'
import { fastParseISO } from '~/utils'

/** The direction/loaded pair that scopes an AppointmentCapacitySet (DEV-7549). */
export interface DirectionScope {
  direction: TransactionDirection
  loaded: boolean
}

export const IMPORT_LOAD: DirectionScope = {
  direction: TransactionDirection.Outbound,
  loaded: true,
}
export const IMPORT_EMPTY: DirectionScope = {
  direction: TransactionDirection.Inbound,
  loaded: false,
}
export const DIRECTION_SCOPES: DirectionScope[] = [IMPORT_LOAD, IMPORT_EMPTY]

export function directionScopeKey(scope: DirectionScope): string {
  return `${scope.direction}-${scope.loaded}`
}

/**
 * Scopes are compared by value, never by identity: a scope read off a
 * CapacitySet came from `directionScopeFromAPI`, so it is a fresh object that
 * is never `===` the IMPORT_LOAD/IMPORT_EMPTY constants.
 */
export function directionScopesEqual(
  a: DirectionScope,
  b: DirectionScope
): boolean {
  return a.direction === b.direction && a.loaded === b.loaded
}

export function directionScopeLabel(scope: DirectionScope): string {
  if (directionScopesEqual(scope, IMPORT_LOAD)) return 'Import Load'
  if (directionScopesEqual(scope, IMPORT_EMPTY)) return 'Empty Return'
  return `${scope.direction} ${scope.loaded ? 'Load' : 'Empty'}`
}

/** The backend defaults a set with no direction/loaded to Import Load — this mirrors that. */
export function directionScopeFromAPI(set: {
  direction?: TransactionDirection
  loaded?: boolean
}): DirectionScope {
  return {
    direction: set.direction ?? IMPORT_LOAD.direction,
    loaded: set.loaded ?? IMPORT_LOAD.loaded,
  }
}

export function makeCapacitySetLabel(
  containerTag: string | null,
  customer: SavedCustomer | null,
  dedicated: boolean,
  portComplex: PortComplex,
  bucketMinutes: number | null = null
): string {
  let label: string
  if (containerTag) {
    label = containerTag
  } else if (customer) {
    label = `${customer.name} ${dedicated ? 'Dedicated' : 'Receiving'}`
  } else {
    label = 'Fleet'
  }
  const base = `${getPortLabel(portComplex)}-${label}`
  if (bucketMinutes !== null) {
    return `${base} (${formatBucketWidth(bucketMinutes)})`
  }
  return base
}

export function formatBucketWidth(bucketMinutes: number): string {
  if (bucketMinutes === 60) return 'per hour'
  if (bucketMinutes === 1440) return 'per day'
  if (bucketMinutes % 60 === 0) return `per ${bucketMinutes / 60} hours`
  return `per ${bucketMinutes} min`
}

export interface CapacitySetIdentifiers {
  customer: Customer | null
  containerTag: string | null
  portComplex: PortComplex
}
// `scope` lives only here, not on the shared interface — buckets don't need it.
export class CapacitySet implements CapacitySetIdentifiers {
  id: number | null
  buckets: CapacityBucket[]
  bucketMinutes: number
  customer: SavedCustomer | null
  customerID: number | null
  containerTag: string | null
  bucketByMinuteOfWeek: Map<number, CapacityBucket>
  dedicated: boolean
  description: string
  key: string
  portComplex: PortComplex
  scope: DirectionScope
  lastEditedBy: SimpleUser | null
  lastEdited: DateTime
  saved: boolean

  constructor(
    id: number | null,
    buckets: CapacityBucket[],
    bucketMinutes: number,
    customer: SavedCustomer | null,
    container_tag: string | null,
    dedicated: boolean,
    portComplex: PortComplex,
    scope: DirectionScope,
    lastEditedBy: SimpleUser | null,
    lastEdited: DateTime,
    saved: boolean
  ) {
    this.id = id
    this.customerID = customer?.id ?? null
    this.customer = customer
    this.containerTag = container_tag
    this.dedicated = dedicated
    this.bucketMinutes = bucketMinutes
    this.scope = scope
    this.description = makeCapacitySetLabel(
      container_tag,
      customer,
      dedicated,
      portComplex,
      bucketMinutes
    )
    this.buckets = buckets.sort(
      (a, b) => a.startMinuteOfWeek - b.startMinuteOfWeek
    )
    this.bucketByMinuteOfWeek = new Map()
    for (const bucket of this.buckets) {
      this.bucketByMinuteOfWeek.set(bucket.startMinuteOfWeek, bucket)
    }
    this.portComplex = portComplex
    this.key = makeCapacitySetKey(this, bucketMinutes)
    this.lastEditedBy = lastEditedBy
    this.lastEdited = lastEdited
    this.saved = saved
  }

  get maxCapacities(): number[] {
    return this.buckets.map((bucket) => bucket.maxAppointments)
  }

  getNearestBucketMinuteOfWeek(time: DateTime) {
    const minuteOfWeek = getMinuteOfWeek(time)
    return Math.floor(minuteOfWeek / this.bucketMinutes) * this.bucketMinutes
  }

  getBucket(time: DateTime): CapacityBucket {
    const nearestBucketMinuteOfWeek = this.getNearestBucketMinuteOfWeek(time)
    const bucket = this.bucketByMinuteOfWeek.get(nearestBucketMinuteOfWeek)
    if (!bucket) {
      const minuteOfWeeks = Array.from(this.bucketByMinuteOfWeek.keys())
      const minuteOfWeek = getMinuteOfWeek(time)
      throw new Error(
        `No bucket found for time ${time.toISO()} (minute of week ${minuteOfWeek})` +
          ` (${
            this.buckets.length
          } buckets defined, last bucket minute of week is ${
            minuteOfWeeks[minuteOfWeeks.length - 1]
          })`
      )
    }
    return bucket
  }

  copy(): CapacitySet {
    const copiedBuckets = this.buckets.map(
      (bucket) =>
        new CapacityBucket(
          {
            startMinuteOfWeek: bucket.startMinuteOfWeek,
            bucketMinutes: bucket.bucketMinutes,
            maxAppointments: bucket.maxAppointments,
            customer: bucket.customer,
            containerTag: bucket.containerTag,
            dedicated: bucket.dedicated,
            portComplex: bucket.portComplex,
          },
          false
        )
    )
    return new CapacitySet(
      this.id,
      copiedBuckets,
      this.bucketMinutes,
      this.customer,
      this.containerTag,
      this.dedicated,
      this.portComplex,
      this.scope,
      this.lastEditedBy,
      this.lastEdited,
      this.saved
    )
  }
}
function getBucketMinuteOfWeek(bucket: GETCapacityBucket): number {
  return bucket.day_of_week * 24 * 60 + bucket.hour_of_day * 60
}

export function makeCapacitySetFromAPI(
  set: GETCapacitySet,
  demoMode: boolean
): CapacitySet {
  const buckets = set.buckets
  const mappedBuckets: CapacityBucket[] = []
  if (buckets.length === 0) {
    throw new Error('No buckets defined for capacity set')
  }
  buckets.sort((a, b) => getBucketMinuteOfWeek(a) - getBucketMinuteOfWeek(b))
  const bucketMinutes = set.bucket_minutes
  const isDedicatedSet = set.independent_capacity || false
  let containerTag: string | null = null
  if (demoMode && set.container_tag) {
    containerTag = faker.commerce.productName()
  } else {
    containerTag = set.container_tag ?? null
  }
  buckets.forEach((bucket) => {
    const currentBucket = new CapacityBucket(
      {
        startMinuteOfWeek: getBucketMinuteOfWeek(bucket),
        bucketMinutes,
        maxAppointments: bucket.max_appointments,
        customer: set.customer ?? null,
        containerTag,
        dedicated: isDedicatedSet,
        portComplex: set.port_complex,
      },
      demoMode
    )
    mappedBuckets.push(currentBucket)
  })
  let lastEditedBy: SimpleUser | null = null
  if (demoMode && set.last_edited_by) {
    lastEditedBy = {
      id: 1,
      first_name: faker.person.firstName(),
      last_name: faker.person.lastName(),
    }
  } else {
    lastEditedBy = set.last_edited_by ?? null
  }

  return new CapacitySet(
    set.id,
    mappedBuckets,
    bucketMinutes,
    set.customer ?? null,
    containerTag,
    isDedicatedSet,
    set.port_complex,
    directionScopeFromAPI(set),
    lastEditedBy,
    fastParseISO(set.last_edited),
    true // If it came from the API, it's saved
  )
}

export function makeCapacitySetKeyFromArgs(
  customerID: number | null,
  containerTag: string | null,
  bucketMinutes: number | null = null
): string {
  let scopeKey: string
  if (customerID) {
    scopeKey = customerID.toString()
  } else if (containerTag) {
    scopeKey = containerTag
  } else {
    scopeKey = 'fleet'
  }
  return bucketMinutes !== null ? `${scopeKey}-${bucketMinutes}` : scopeKey
}

export function makeCapacitySetKey(
  set: CapacitySetIdentifiers,
  bucketMinutes: number | null = null
): string {
  return makeCapacitySetKeyFromArgs(
    set.customer?.id ?? null,
    set.containerTag,
    bucketMinutes
  )
}

export type DayOfWeek =
  | 'Saturday'
  | 'Sunday'
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'

export function minuteOfWeekToDayOfWeek(minuteOfWeek: number): DayOfWeek {
  const dayOfWeek = Math.floor(minuteOfWeek / 1440)
  switch (dayOfWeek) {
    case 0:
      return 'Sunday'
    case 1:
      return 'Monday'
    case 2:
      return 'Tuesday'
    case 3:
      return 'Wednesday'
    case 4:
      return 'Thursday'
    case 5:
      return 'Friday'
    case 6:
      return 'Saturday'
    default:
      throw new Error('Invalid minute of week')
  }
}
export class CapacitySetInfo implements CapacitySetIdentifiers {
  customer: Customer | null
  customerID: number | null
  containerTag: string | null
  dedicated: boolean
  description: string
  setDescription: string
  key: string
  portComplex: PortComplex
  bucketMinutes: number | null

  constructor(
    {
      customer,
      containerTag,
      dedicated,
      portComplex,
      bucketMinutes = null,
    }: {
      customer: SavedCustomer | Customer | null
      containerTag: string | null
      dedicated: boolean
      portComplex: PortComplex
      bucketMinutes?: number | null
    },
    demoMode: boolean
  ) {
    // Re-wrap raw API customers; if a Customer model is already passed in
    // (e.g. CapacityBucket forwarding from a CapacitySet) we keep the same
    // instance to avoid generating a fresh faker name in demo mode.
    if (customer instanceof Customer) {
      this.customer = customer
    } else {
      this.customer = customer ? new Customer(customer, demoMode) : null
    }
    this.customerID = customer?.id ?? null
    this.containerTag = containerTag
    this.dedicated = dedicated
    this.bucketMinutes = bucketMinutes
    this.description = makeCapacitySetLabel(
      containerTag,
      customer,
      dedicated,
      portComplex,
      bucketMinutes
    )
    this.key = makeCapacitySetKeyFromArgs(
      customer?.id ?? null,
      containerTag,
      bucketMinutes
    )
    this.setDescription = this.description
    this.portComplex = portComplex
  }
}
export class CapacityBucket extends CapacitySetInfo {
  maxAppointments: number
  startMinuteOfWeek: number
  endMinuteOfWeek: number
  bucketMinutes: number
  key: string
  constructor(
    {
      startMinuteOfWeek,
      bucketMinutes,
      maxAppointments,
      customer,
      containerTag,
      dedicated,
      portComplex,
    }: {
      startMinuteOfWeek: number
      bucketMinutes: number
      maxAppointments?: number
      customer: Customer | null
      containerTag: string | null
      dedicated: boolean
      portComplex: PortComplex
    },
    demoMode: boolean
  ) {
    super(
      { customer, containerTag, dedicated, portComplex, bucketMinutes },
      demoMode
    )
    this.maxAppointments = maxAppointments ?? 0
    this.startMinuteOfWeek = startMinuteOfWeek
    this.endMinuteOfWeek = startMinuteOfWeek + bucketMinutes
    this.bucketMinutes = bucketMinutes
    // this key needs to include port complex to ensure we don't overwrite buckets for
    // the same customer/container tag in different port complexes
    this.key = `${startMinuteOfWeek}-${this.portComplex}-${makeCapacitySetKey(
      this,
      bucketMinutes
    )}`
  }

  get dayOfWeek() {
    let day_of_week: number = Math.floor(this.startMinuteOfWeek / (24 * 60))
    if (day_of_week === 7) {
      day_of_week = 0
    }
    return day_of_week
  }

  get dayOfWeekDescription(): DayOfWeek {
    return minuteOfWeekToDayOfWeek(this.startMinuteOfWeek)
  }

  get hourOfDay() {
    return Math.floor((this.startMinuteOfWeek % (24 * 60)) / 60)
  }

  get timeDescription(): string {
    const exampleStart = convertMinuteOfWeekToTime(
      this.startMinuteOfWeek,
      DateTime.now()
    )
    const exampleEnd = exampleStart.plus({ minutes: this.bucketMinutes })
    return `${exampleStart.toFormat('ccc H:mm')} - ${exampleEnd.toFormat('H:mm')}`
  }

  addCapacity(change: number) {
    this.maxAppointments = Math.max(this.maxAppointments + change, 0)
  }

  getStart(timeForWeek: DateTime): DateTime {
    return convertMinuteOfWeekToTime(this.startMinuteOfWeek, timeForWeek)
  }

  getEnd(timeForWeek: DateTime): DateTime {
    return convertMinuteOfWeekToTime(this.endMinuteOfWeek, timeForWeek)
  }

  getStartFromTimeInBucket(timeWithinBucket: DateTime): DateTime {
    const minuteOfWeek = getMinuteOfWeek(timeWithinBucket)
    if (minuteOfWeek < this.startMinuteOfWeek) {
      throw new Error(
        `Absolute time is before the start of the bucket. ` +
          `Absolute time: ${timeWithinBucket.toISO()}, bucket start: ${
            this.startMinuteOfWeek
          }`
      )
    } else if (minuteOfWeek > this.endMinuteOfWeek) {
      throw new Error(
        `Absolute time is after the end of the bucket. ` +
          `Absolute time: ${timeWithinBucket.toISO()} (minuteOfWeek=${minuteOfWeek}). Bucket end: minuteOfWeek=${
            this.endMinuteOfWeek
          }`
      )
    }
    const diff = this.startMinuteOfWeek - minuteOfWeek
    return timeWithinBucket.minus({ minutes: diff })
  }

  getEndOfBucket(absoluteTime: DateTime): DateTime {
    return this.getStartFromTimeInBucket(absoluteTime).plus({
      minutes: this.bucketMinutes,
    })
  }

  getAbsoluteBucketWindow(absoluteTime: DateTime): [DateTime, DateTime] {
    return [
      this.getStartFromTimeInBucket(absoluteTime),
      this.getEndOfBucket(absoluteTime),
    ]
  }
}

export function getMinuteOfWeek(time: DateTime): number {
  let weekDay: number = time.weekday // Monday is 1, Sunday is 7
  if (weekDay === 7) {
    weekDay = 0
  }
  if (weekDay > 6) {
    throw new Error(
      `Weekday is greater than 6 (${weekDay}). This is probably a bug in luxon. ` +
        `Time: ${time.toISO()}`
    )
  }
  return (weekDay * 24 + time.hour) * 60 + time.minute
}

export class CategorizedCapacitySets {
  fleetSets: Map<string, CapacitySet>
  customerSets: Map<string, CapacitySet>
  tagSets: Map<string, CapacitySet>
  allSets: Map<string, CapacitySet>

  constructor(
    fleetSets: Map<string, CapacitySet>,
    customerSets: Map<string, CapacitySet>,
    tagSets: Map<string, CapacitySet>
  ) {
    this.fleetSets = fleetSets
    this.customerSets = customerSets
    this.tagSets = tagSets
    this.allSets = new Map([
      ...fleetSets.entries(),
      ...customerSets.entries(),
      ...tagSets.entries(),
    ])
  }
}
